import { RedirectFunction, Router } from '@angular/router';
import { inject } from '@angular/core';
import { FeatureAccessRightsService } from '@app/shared/features/access-rights/services/feature-access-rights.service';
import { UserDetailsResponse } from '@api-client/mobipay';
import { map } from 'rxjs';
import { ROUTES } from '@app/app.routes-constants';
import FeaturesEnum = UserDetailsResponse.FeaturesEnum;


/**
 * Determines the user's initial landing page path based on their feature access rights.
 *
 * This function is used in the `redirectTo` property of a route configuration.
 * It checks asynchronously if the user has access to the 'TodoTransactions' feature
 * and redirects accordingly.
 *
 * @returns An Observable that emits a UrlTree, guiding the router to the
 * appropriate destination path.
 * - If access is granted: redirects to the To-Do Transactions page.
 * - If access is denied: redirects to the Outbound Invoices page.
 *
 * @usage
 * ```typescript
 * // In your route configuration (e.g., app.routes.ts):
 * {
 * path: '',
 * pathMatch: 'full',
 * redirectTo: determineLandingPagePath
 * }
 * ```
 */
export const determineDefaultLandingPagePath: RedirectFunction = () => {
  // DI
  const activeFeatures = inject(FeatureAccessRightsService);
  const router = inject(Router);

  return activeFeatures.has$(FeaturesEnum.TodoTransactions).pipe(
    map(hasAccess =>
      hasAccess
        ? router.createUrlTree([ROUTES.TRANSACTIONS.TODO])
        : router.createUrlTree([ROUTES.INVOICES.INBOUND])
    )
  );
};
